import { fullSnapshotEvent, ReplayerEvents } from '@rrweb/types'
import { Replayer } from 'rrweb'
import { BaseVideoPlayer } from './players/BaseVideoPlayer'
import { initializeTopLevelDiv, isErtDebugModeEnabled, getVideoIdentifier } from '../rrwebUtils'
import {
  ProcessedRRWebEvents,
  ReplayerControlState,
  VideoEndEvent,
  VideoMirrorEndEvent,
  VideoMirrorStartEvent,
  VideoStartEvent,
  VideoStateChangeEvent
} from '../types'
import { DeferredWorkOperation, PlaceholderWorkQueue } from './placeholderTracking/PlaceholderWorkQueue'
import { ReplayerCurrentTime, ReplayerProxy } from './ReplayerProxy'
import { WorkQueue } from './util/WorkQueue'
import { Camera } from '../../../../../api/models'
import { BufferingCoordinator } from './util/BufferingCoordinator'
import { VideoPreloader } from './util/VideoPreloader'
import { sortVideosByMostRecentPlayEvent } from './players/videoPlayerUtils'
import { VideoMirror } from './mirroring/VideoMirror'
import { TrackedElementDetails } from './placeholderTracking/TrackedElementDetails'
import { getMostRecentVideoMirrorEvent } from './mirroring/videoMirrorUtils'
import { RRWEB_EVENTS } from '@simplisafe/compass-web'
import { LoggerName, namedLogger } from '../../../../../utils/logger'

const logger = namedLogger(LoggerName.ErtPlugin)

export class ErtPlugin {
  // dom manipulations involving video placeholders that are not yet tracked have to be deferred. we hold them in this map.
  private placeholderWorkQueue = new PlaceholderWorkQueue()
  private onFlushWorkQueue = new WorkQueue()

  private replayerControlState: ReplayerControlState = {
    muted: false,
    volume: 1,
    speed: 1
  }

  private readonly camerasById: Map<string, Camera>
  private readonly videoPreloader: VideoPreloader
  private readonly bufferingCoordinator: BufferingCoordinator
  private readonly processedRRWebEvents: ProcessedRRWebEvents
  private readonly videos: Map<string, BaseVideoPlayer>
  private readonly videoMirrors: Map<string, VideoMirror>
  private readonly replayerProxy: ReplayerProxy

  constructor(processedEvents: ProcessedRRWebEvents, camerasById: Map<string, Camera>, playerDocument: Document, replayer: Replayer) {
    logger.debug('ErtPlugin constructed')

    this.processedRRWebEvents = processedEvents
    this.camerasById = camerasById

    this.replayerProxy = new ReplayerProxy(replayer, playerDocument, this.bufferVideosBeforeReplayerStarts)
    this.videoPreloader = new VideoPreloader(this.camerasById)
    this.videos = this.videoPreloader.appendVideosToBodyForPreloading(playerDocument, this.processedRRWebEvents)
    this.videoMirrors = this.initVideoMirrors(this.processedRRWebEvents)
    // listen for when videos are buffering and emit waiting start and waiting end events
    this.bufferingCoordinator = new BufferingCoordinator(this.replayerProxy, Array.from(this.videos.values()))

    /**
     * register listeners for all the replayer events
     */

    replayer.on(ReplayerEvents.Pause, () => {
      logger.debug('Replayer Pause called')
      this.handlePause()
      this.printVideoDetails()
    })
    replayer.on(ReplayerEvents.Resume, () => {
      logger.debug('Replayer Resume called')
      this.handlePlay()
      this.printVideoDetails()
    })
    replayer.on(ReplayerEvents.PlayBack, () => {
      // this fires whenever the user skips backward in the recording
      logger.debug(
        'Replayer PlayBack called - this.replayerProxy.currentTime:',
        { console: this.replayerProxy.getReplayerCurrentTime() },
        '- this.replayerProxy.UNSAFE_getReplayerCurrentTime():',
        this.replayerProxy.UNSAFE_getReplayerCurrentTime()
      )
      this.printVideoDetails()
    })
    replayer.on(ReplayerEvents.Start, () => {
      // this fires whenever play is called on the replayer
      logger.debug('Replayer Start called')
      this.handlePlay()
      this.printVideoDetails()
    })
    replayer.on(ReplayerEvents.SkipStart, () => {
      // if skip inactive is enabled, this will fire when the inactive period begins
      logger.debug('Replayer SkipStart called')
      //pause all the videos when the skip starts.
      this.handlePause()
    })
    replayer.on(ReplayerEvents.SkipEnd, () => {
      // if skip inactive is enabled, this will fire when the inactive period ends
      logger.debug('Replayer SkipEnd called')
      //play all the videos when the skip ends.
      this.handlePlay()
    })
    replayer.on(ReplayerEvents.Finish, () => {
      logger.debug('Replayer Finish called')
      this.handlePause()
      this.printVideoDetails()
    })
    replayer.on(ReplayerEvents.Destroy, () => {
      logger.debug('Replayer Destroy called')
    })
    replayer.on(ReplayerEvents.Flush, () => {
      logger.debug('Replayer Flush called')
      this.onFlush()
    })

    if (isErtDebugModeEnabled()) {
      // @ts-expect-error - this is for debugging purposes only
      window.placeholderWorkQueue = this.placeholderWorkQueue
      // @ts-expect-error - this is for debugging purposes only
      window.onFlushWorkQueue = this.onFlushWorkQueue
      // @ts-expect-error - this is for debugging purposes only
      window.processedRRWebEvents = processedEvents
      // @ts-expect-error - for debugging purposes only
      window.videos = this.videos
      // @ts-expect-error - for debugging purposes only
      window.printVideoDetails = this.printVideoDetails
      this.printVideoDetails()
    }
  }

  private initVideoMirrors(processedRRWebEvents: ProcessedRRWebEvents) {
    const videoMirrors: Map<string, VideoMirror> = new Map()
    for (const videoMirrorName of processedRRWebEvents.videoMirrorPlaceholderNames) {
      logger.debug('Initializing video mirror named', videoMirrorName)
      videoMirrors.set(videoMirrorName, new VideoMirror(videoMirrorName))
    }
    return videoMirrors
  }

  private checkForVideoMirrorPlaceholder = (el: HTMLElement) => {
    const mirrorName = el.parentElement?.dataset?.[TrackedElementDetails.VIDEO_MIRROR_PLACEHOLDER.attributeCamelName]
    if (mirrorName) {
      const videoMirror = this.videoMirrors.get(mirrorName)
      if (videoMirror) {
        videoMirror.initializeCanvas(el.parentElement)
        const mirrorEvent = getMostRecentVideoMirrorEvent(
          mirrorName,
          this.processedRRWebEvents.videoMirrorEvents,
          this.replayerProxy.getReplayerCurrentTime()
        )
        if (mirrorEvent) {
          const shouldBeMirroring = mirrorEvent?.eventName === RRWEB_EVENTS.VIDEO_MIRROR_START
          const videoKey = getVideoIdentifier(mirrorEvent)
          const videoToMirror = this.videos.get(videoKey)
          if (videoToMirror) {
            // we always replace and restart the mirror whenever we encounter it. It will be been disconnected by a full snapshots often and will need to be reconnected.
            logger.debug('checkForVideoMirrorPlaceholder - is reestablishing mirror of', videoKey, 'to', mirrorName)
            videoMirror.reestablishMirrorInPlaceholder(videoToMirror, shouldBeMirroring)
          } else {
            logger.debug('checkForVideoMirrorPlaceholder - could not find video for mirrorName', mirrorName, videoKey)
          }
        } else {
          logger.debug('checkForVideoMirrorPlaceholder - did not find a mirror event for mirrorName', mirrorName)
        }
      } else {
        logger.debug('checkForVideoMirrorPlaceholder - encountered a video mirror placeholder with no associated video mirror events. skipping', {
          console: {
            el
          }
        })
      }
    }
  }

  public elementBuilt(el: HTMLElement) {
    this.placeholderWorkQueue.checkForVideoPlaceholderAndExecuteAnyDeferredWork(el)
    this.checkForVideoMirrorPlaceholder(el)
  }

  /**
   * This is part of a hack to get around the fact that the rrweb replayer doesn't reliably provide the current time when seeking
   * @param currentTimeInSeconds
   */
  public saveCurrentTime(currentTimeInSeconds: number) {
    this.replayerProxy.currentTime = currentTimeInSeconds * 1000
  }
  public handleVolumePercentageChange(fraction: number) {
    this.replayerControlState.volume = fraction
    this.doForAllVideos(`setVolume(${fraction})`, player => player.setVolume(fraction))
  }
  public handleSpeedChange(speedMultiplier: number): void {
    this.replayerControlState.speed = speedMultiplier
    this.doForAllVideos(`setSpeed(${speedMultiplier})`, player => player.setSpeed(speedMultiplier))
  }

  /**
   * This is called when the user seeks to a new time in the recording. It is called right before the replayer.goto function is called.
   * @param seekToTimeMs
   */
  public beforeSeek(seekToTimeMs: number) {
    logger.debug('beforeSeek called -', seekToTimeMs)

    // notify the buffering handler that we are seeking
    this.bufferingCoordinator.beforeSeek()

    // When we seek, a full snapshot may be processed which will rebuild the entire dom and will make all of our previously tracked video placeholders stale.
    // Before we process the seek, we clear them so that we don't hold onto to stale references.
    this.placeholderWorkQueue.clearTrackedVideoPlaceholders()

    // if there is any pending work abandon it before we seek. Otherwise, it will be processed after the seek and could cause problems.
    this.onFlushWorkQueue.clear()

    // the replayer currentTime gets reset sometimes when we seek, so we update before (here) and after the rrweb processes the sync (below)
    this.replayerProxy.currentTime = seekToTimeMs

    this.onFlushWorkQueue.queueWork(() => {
      // the replayer currentTime gets reset sometimes when we seek, so we update before (above) and after the rrweb processes the sync (here)
      this.replayerProxy.currentTime = seekToTimeMs

      logger.debug('beforeSeek onFlushWorkQueue updated the currentTime on the replayerProxy-', seekToTimeMs)
    })
    logger.debug('beforeSeek finished -', seekToTimeMs)
  }

  /**
   * This is called when the user seeks to a new time in the recording. It is called right after the replayer.goto function is called
   * @param seekToTimeMs
   */
  public afterSeek(seekToTimeMs: number) {
    // if we've scheduled any work that needs to happen when the seek completes.
    logger.debug('afterSeek called - processing work queue -', seekToTimeMs)

    // notify the buffering handler that we are done seeking
    this.bufferingCoordinator.afterSeek()
  }

  public onFlush() {
    // if we've scheduled any work that needs to happen when the player flushes, we complete it now. The replayer will flush when it seeks to a new time on the scrubber.
    logger.debug('onFlush processing work queue')
    this.onFlushWorkQueue.completePendingWork()
  }

  /**
   * Registers a callback to be called when buffering handler detects that buffering has started
   * @param callback
   */
  public onBufferingStart(callback: (wasPlayingBeforeBufferingStarted: boolean) => void) {
    this.bufferingCoordinator.onBufferingStart(callback)
  }

  /**
   * Registers a callback to be called when buffering handler detects that buffering has ended
   * @param callback
   */
  public onBufferingEnd(callback: () => void) {
    this.bufferingCoordinator.onBufferingEnd(callback)
  }

  /**
   * use this to register a ready callback. The player will emit the ready event with all the embedded videos have emitted a "canplaythrough" event
   * @param ready
   */
  public onReady(ready: () => void): void {
    this.videoPreloader.onReady(ready)
  }

  private handlePlay() {
    this.doForAllVideos('syncVideoStateWhenReplayerStarts', player =>
      player.syncVideoStateAfterReplayerStarts(
        this.placeholderWorkQueue,
        this.replayerControlState,
        this.replayerProxy,
        this.replayerProxy.getReplayerCurrentTime()
      )
    )
  }

  /**
   * This is used by the replayer proxy before it resumes after pausing for buffering.
   * It is intentionally declared as a private const so that a fat arrow function can be used
   * that is automatically bound to 'this'.
   * @param replayerProxy
   * @param replayerCurrentTime
   */
  private bufferVideosBeforeReplayerStarts = async (replayerProxy: ReplayerProxy, replayerCurrentTime: ReplayerCurrentTime) => {
    logger.debug('bufferVideosBeforeReplayerStarts - begin')

    const promises: Promise<void>[] = []

    if (this.videos) {
      for (const video of this.videos.values()) {
        promises.push(video.bufferVideoBeforeReplayerStarts(replayerProxy, replayerCurrentTime))
      }
    }

    await Promise.all(promises)
    logger.debug('bufferVideosBeforeReplayerStarts - end')
  }

  private handlePause() {
    // passing control to the buffering handler to decide how to pause the videos because pause needs to be handled differently when buffering
    this.bufferingCoordinator.handlePause()
  }

  private doForAllVideos(actionName: string, callback: (videoPlayer: BaseVideoPlayer) => void) {
    logger.debug('doForAllVideos begin -', actionName)
    if (!this.replayerProxy) {
      throw new Error('doForAllVideos was called before replayerState was initialized')
    }
    if (this.videos) {
      for (const video of this.videos.values()) {
        callback(video)
      }
    }
    logger.debug('doForAllVideos end -', actionName)
  }

  // I needed to bind this to the class instance to make it work when assigned to window.printVideoDetails for debugging
  private printVideoDetails = () => {
    if (isErtDebugModeEnabled()) {
      let videoDetails = 'Replayer Details:\n'
      videoDetails += `\t${this.replayerProxy.isPlaying() ? 'plying' : 'paused'} - time: ${this.replayerProxy.currentTime}\n`
      videoDetails += 'Video Details:\n'
      for (const video of this.videos.values()) {
        videoDetails += `\t${video.getVideoDetails()}\n`
      }
      logger.debug(videoDetails)
    }
  }

  public setReplayer(replayer: Replayer) {
    this.replayerProxy.setReplayer(replayer)
  }

  public async processVideoStartEvent(videoStartEvent: VideoStartEvent) {
    const { replayerProxy } = this

    // put the work that needs to be done to handle the start event into a function so that it can be deferred if needed until the video placeholder is added to the dom
    const handleVideoStartEvent = async (videoPlaceholderNode?: HTMLElement) => {
      const videoIdentifier = getVideoIdentifier(videoStartEvent)

      const videoPlayer = this.videos.get(videoIdentifier)

      if (videoPlayer) {
        videoPlayer.applyVideoStateEvent(videoStartEvent, replayerProxy, replayerProxy.getReplayerCurrentTime(), this.replayerControlState)
        if (videoPlaceholderNode) {
          videoPlayer.showVideo(videoPlaceholderNode, replayerProxy.getReplayerCurrentTime())
        }
      } else {
        logger.error(`Error: The video player for videoStartEvent ${videoIdentifier} is missing from this.videos.`)
      }
    }

    this.placeholderWorkQueue.doWorkForVideoPlaceholderWhenReady(videoStartEvent.videoPlaceholderName, replayerProxy, {
      operation: DeferredWorkOperation.ShowVideo,
      callback: handleVideoStartEvent
    })
  }

  public processVideoEndEvent(videoEndEvent: VideoEndEvent) {
    const { replayerProxy } = this

    // put the work that needs to be done to handle the end event into a function so that it can be deferred if needed until the video placeholder is added to the dom
    const handleVideoEndEvent = async () => {
      const videoIdentifier = getVideoIdentifier(videoEndEvent)
      const video = this.videos.get(videoIdentifier)
      if (video) {
        video.hideAndPauseVideo()
      } else {
        logger.error(`Error: The video player for videoEndEvent ${videoIdentifier} is missing from this.videos.`)
      }
    }

    this.placeholderWorkQueue.doWorkForVideoPlaceholderWhenReady(videoEndEvent.videoPlaceholderName, replayerProxy, {
      operation: DeferredWorkOperation.HideVideo,
      callback: handleVideoEndEvent
    })
  }

  public processVideoStateChangeEvent(videoStateChangeEvent: VideoStateChangeEvent) {
    const { replayerProxy } = this

    // put the work that needs to be done to handle the state change event into a function so that it can be deferred if needed until the video placeholder is added to the dom
    const handleVideoStateChangeEvent = () => {
      const videoIdentifier = getVideoIdentifier(videoStateChangeEvent)
      const video = this.videos.get(videoIdentifier)
      if (video) {
        video.applyVideoStateEvent(videoStateChangeEvent, replayerProxy, replayerProxy.getReplayerCurrentTime(), this.replayerControlState)
      } else {
        logger.error(`Error: The video player for videoStateChangeEvent ${videoIdentifier} is missing from this.videos.`)
      }
    }

    this.placeholderWorkQueue.doWorkForVideoPlaceholderWhenReady(videoStateChangeEvent.videoPlaceholderName, replayerProxy, {
      operation: DeferredWorkOperation.VideoStateChange,
      callback: handleVideoStateChangeEvent
    })
  }

  public processVideoMirrorStartEvent(videoMirrorStartEvent: VideoMirrorStartEvent) {
    const videoIdentifier = getVideoIdentifier(videoMirrorStartEvent)
    const videoPlayer = this.videos.get(videoIdentifier)
    const videoMirror = this.videoMirrors.get(videoMirrorStartEvent.videoMirrorPlaceholderName)
    if (!videoPlayer) {
      logger.error(`processVideoMirrorStartEvent - The video player for ${videoIdentifier} is missing from this.videos.`)
    } else if (!videoMirror) {
      logger.error(
        `processVideoMirrorStartEvent - The video videoMirror for videoCanvasName ${videoMirrorStartEvent.videoMirrorPlaceholderName} is missing from this.videoMirrors.`
      )
    } else {
      videoMirror.startMirroring(videoPlayer)
    }
  }

  public processVideoMirrorEndEvent(videoMirrorEndEvent: VideoMirrorEndEvent) {
    const videoMirror = this.videoMirrors.get(videoMirrorEndEvent.videoMirrorPlaceholderName)
    if (!videoMirror) {
      logger.error(
        `processVideoMirrorEndEvent - The video videoMirror for videoCanvasName ${videoMirrorEndEvent.videoMirrorPlaceholderName} is missing from this.videoMirrors.`
      )
    } else {
      videoMirror.stopMirroring()
    }
  }

  public processClearVideoPlaceholdersEvent() {
    this.placeholderWorkQueue.clearTrackedVideoPlaceholders()
  }

  public processFullSnapshotEvent(event: fullSnapshotEvent, isSync: boolean) {
    logger.debug('processFullSnapshotEvent isSync', isSync)

    const { replayerProxy } = this

    // check to make sure the embedded videos are initialized (this should have happened in the initializeReplayPlugin method)

    // if the document node was replaced by the full snapshot, we need to update our reference to it
    this.replayerProxy.updatePlayerDocument(event)

    const reattachVideos = () => {
      this.reAttachVideosAfterFullSnapshot(this.replayerProxy.getPlayerDocument(), replayerProxy)
    }

    // if this is a synchronous event (which it will be when seeking), queue the work to be done when the seeking is complete (which will be the next time the replayer triggers a flush event)
    if (isSync) {
      this.onFlushWorkQueue.queueWork(reattachVideos)
    } else {
      reattachVideos()
    }
  }

  /**
   * After processing a full snapshot, the dom is wiped out. We need to append the embedded videos back to the body and resume them
   * @param document
   * @param replayerProxy
   * @private
   */
  private reAttachVideosAfterFullSnapshot(document: Document | undefined, replayerProxy: ReplayerProxy): void {
    if (!document) {
      throw new Error('reAttachVideosAfterFullSnapshot could not locate the player document')
    }

    const replayerCurrentTime = replayerProxy.getReplayerCurrentTime()

    logger.debug('reAttachVideosAfterFullSnapshot - replayerCurrentTime', { console: replayerCurrentTime })
    if (this.videos?.values()) {
      // If two videos are instrumented to display in the same placeholder (as we have with the main video canvas in holodeck, we will have multiple
      // videos that think they should be in the placeholder at the same time, and we need to make sure the right one is on top.
      // We fix do this by ordering the videos by start time so that the one that started playing most recently ends up on top in the placeholder.
      const videosSortedByLatestStartTime = sortVideosByMostRecentPlayEvent(Array.from(this.videos.values()), replayerCurrentTime)

      for (const video of videosSortedByLatestStartTime) {
        // look for the embedded video container div in the document body so that we know if it needs to be re-attached
        const alreadyEmbeddedDiv = document.getElementById(video.getVideoIdentifier())
        if (!alreadyEmbeddedDiv) {
          const topLevelDiv = document.createElement('div')
          document.body.appendChild(topLevelDiv)
          initializeTopLevelDiv(topLevelDiv, video)
          video.reAttachToNewInitialParentAfterFullSnapshot(topLevelDiv)
        }

        const isVisible = video.isVisibleAtReplayerCurrentTime(replayerCurrentTime)
        logger.debug('video isVisible', isVisible, video.getVideoIdentifier())
        if (isVisible) {
          const showAndResumeVideo = async (videoPlaceholderNode?: HTMLElement) => {
            if (videoPlaceholderNode) {
              await video.showAndResumeAfterFullSnapshot(videoPlaceholderNode, replayerProxy)
            }
          }

          this.placeholderWorkQueue.doWorkForVideoPlaceholderWhenReady(video.getVideoPlaceholderName(), replayerProxy, {
            operation: DeferredWorkOperation.ShowVideo,
            callback: showAndResumeVideo
          })
        } else {
          // if the video is not visible at the current time, we hide it
          video.hideAndPauseVideo()
        }
      }
    }
  }
}
